import React, { createContext, FC, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { SnackbarProps } from '@mui/material/Snackbar';

const DEFAULT_DURATION = 5000;

// helpful example
//
// import useToastContext from '../hooks/useToastContext';
// ...
// const { showToast, hideToast } = useToastContext();
// ...
// show a success toast with 'Good Job!' message for 8 seconds, then run log
// showToast('success', 'Good Job!', 8000, () => console.log('banana'));
// ...
// programatically kill the toast if desired
// hideToast();

interface IToastConfig extends SnackbarProps {
  variant: 'error' | 'info' | 'success' | 'warning';
  message: any;
  customDuration?: number;
  onClose?: () => void;
}

type ToastContextType = {
  toastConfig: IToastConfig | null;
  showToast: (
    variant: 'error' | 'info' | 'success' | 'warning',
    message: any,
    customDuration?: number,
    onClose?: () => void
  ) => void;
  hideToast: () => void;
};

// default toastContext
export const ToastContext = createContext<ToastContextType | null>(null);

interface IToastContextProviderProps {
  children: React.ReactNode;
}

export const ToastContextProvider: FC<IToastContextProviderProps> = ({ children }) => {
  const classes = useStyles();
  // control the toast
  // toastConfig is null by default - toast is hidden
  const [toastConfig, setToastConfig] = useState<IToastConfig | null>(null);

  // make toast
  const showToast = (
    variant: 'error' | 'info' | 'success' | 'warning',
    message: string,
    customDuration?: number,
    onClose?: () => void
  ) => {
    const finalMessage =
      typeof message === 'string' ? message : 'An error occurred, please try again.';
    setToastConfig({ variant, message: finalMessage, customDuration, onClose });
  };

  // trash the toast
  const hideToast = () => {
    // run callback if it exists
    if (toastConfig?.onClose) {
      toastConfig.onClose();
    }
    setToastConfig(null);
  };

  return (
    <ToastContext.Provider value={{ toastConfig, showToast, hideToast }}>
      {children}
      {toastConfig && (
        <Snackbar
          key={toastConfig.message}
          className={classes.toast}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
          autoHideDuration={toastConfig.customDuration || DEFAULT_DURATION}
          onClose={() => hideToast()}
          open={true}
        >
          <Alert onClose={() => hideToast()} severity={toastConfig.variant} variant="filled">
            {toastConfig.message}
          </Alert>
        </Snackbar>
      )}
    </ToastContext.Provider>
  );
};

const useStyles = makeStyles(() => ({
  toastContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  toast: {
    bottom: '68px',
  },
}));
